import { useRegisterSW } from "virtual:pwa-register/react";
import { Download, RefreshCw, Smartphone, X } from "lucide-react";
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Register SW auto-update hook
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log("SW Registered:", r);
    },
    onRegisterError(error) {
      console.error("SW Registration error:", error);
    },
  });

  useEffect(() => {
    // Detect iOS
    const ua = window.navigator.userAgent;
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream: boolean }).MSStream;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone;

    if (isIOSDevice && !isStandalone) {
      setIsIOS(true);
    }

    // Listen for BeforeInstallPrompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setDeferredPrompt(null);
      }
    } else if (isIOS) {
      setShowIOSPrompt(true);
    }
  };

  return (
    <>
      {/* SW New Version Refresh Toast Banner */}
      {needRefresh && (
        <div className="fixed bottom-5 right-5 z-[100] max-w-md bg-zinc-900 text-white p-4 rounded-xl shadow-2xl border border-zinc-700 flex items-center justify-between gap-4 animate-bounce">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
            <div>
              <p className="text-xs font-bold text-white">Nueva versión disponible</p>
              <p className="text-[11px] text-zinc-300">
                Actualiza para aplicar las últimas mejoras.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateServiceWorker(true)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors"
            >
              Actualizar
            </button>
            <button
              onClick={() => setNeedRefresh(false)}
              className="p-1 text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* PWA Install Button Banner for Desktop / Mobile browsers */}
      {(deferredPrompt || isIOS) && !dismissed && (
        <div className="fixed bottom-5 left-5 z-[90] bg-white rounded-xl shadow-xl border border-zinc-200 p-3.5 max-w-xs flex items-center gap-3 animate-drawer-in">
          <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow-xs flex-shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-zinc-900 leading-tight">Instalar TUKI RAD</p>
            <p className="text-[11px] text-zinc-500 truncate">Accede más rápido como app nativa</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleInstallClick}
              className="px-2.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white rounded-md text-xs font-medium transition-colors flex items-center gap-1"
            >
              <Download className="w-3 h-3" />
              Instalar
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 text-zinc-400 hover:text-zinc-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* iOS Instructions Modal */}
      {showIOSPrompt && (
        <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-2xl border border-zinc-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-zinc-900">Instalar en iOS</h3>
              <button
                onClick={() => setShowIOSPrompt(false)}
                className="text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Para instalar **TUKI RAD** en tu iPhone o iPad:
            </p>
            <ol className="text-xs text-zinc-700 space-y-2 list-decimal list-inside bg-zinc-50 p-3 rounded-lg border border-zinc-200">
              <li>Toca el botón **Compartir** en la barra de Safari.</li>
              <li>Desplázate hacia abajo y selecciona **"Agregar a inicio"**.</li>
              <li>Confirma presionando **"Agregar"**.</li>
            </ol>
            <button
              onClick={() => setShowIOSPrompt(false)}
              className="w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}
